/**
 * Helpers for opening uploaded files (comment attachments, documents, action
 * links). Only formats the browser can render safely are previewed in the app;
 * everything else (xls/xlsx, csv, doc/docx, ppt/pptx, zip/rar, audio, ...) is
 * handed to the user as a download.
 *
 * Lives outside `Utils/index.ts` because it needs the API client, and the API
 * client already imports from `Utils/index.ts`.
 */
import api from "../../Network/ApiService";
import { envConf } from "../../Config";
import { isImageFile, isPdfFile } from "./index";

/** File name from the last path segment of a URL, decoded, without the query string. */
export const getFileNameFromUrl = (url: string): string => {
  try {
    const path = url.split("?")[0].split("#")[0];
    return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
  } catch {
    return "";
  }
};

export const getFileExtension = (url: string): string => {
  const name = getFileNameFromUrl(url);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.substring(dot + 1).toLowerCase() : "";
};

/** Plain-text formats rendered in the preview modal as text. */
const TEXT_PREVIEW_EXTENSIONS = ["txt", "md", "log"];

export const isTextFile = (url: string): boolean => {
  if (!url) return false;
  return TEXT_PREVIEW_EXTENSIONS.includes(getFileExtension(url));
};

/** Whether the file can be shown in the in-app preview modal. */
export const isPreviewableFile = (url: string): boolean => {
  return isImageFile(url) || isPdfFile(url) || isTextFile(url);
};

/** Uploaded files are served through the authenticated file proxy. */
export const getSecureFileUrl = (url: string): string => {
  if (!url || !url.includes("amazonaws.com")) return url;
  const baseUrl = envConf.apiBaseUrl.replace(/\/$/, "");
  // S3 URLs may already have encoded characters (e.g. %40 for @, %20 for space).
  // Decode first to normalize, then re-encode cleanly to avoid double-encoding (%2540, %2520 etc.)
  const cleanUrl = encodeURIComponent(decodeURIComponent(url));
  return `${baseUrl}/file_proxy/?url=${cleanUrl}`;
};

/** Download name: the given title, with the real file extension guaranteed. */
export const getDownloadFileName = (fileUrl: string, fileTitle: string): string => {
  const ext = getFileExtension(fileUrl);
  const base =
    (fileTitle || "").trim() || getFileNameFromUrl(fileUrl) || "document";
  if (!ext || base.toLowerCase().endsWith(`.${ext}`)) return base;
  return `${base}.${ext}`;
};

const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser a moment to start the download before revoking.
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 10000);
};

/**
 * Downloads a file with its original name. Uploaded files go through the
 * file proxy (so private buckets and permissions are honoured); anything else
 * is fetched directly, falling back to opening it in a new tab when the host
 * does not allow that.
 */
export const downloadFile = async (
  fileUrl: string,
  fileName: string
): Promise<void> => {
  if (!fileUrl) return;
  const name = getDownloadFileName(fileUrl, fileName);
  const secureUrl = getSecureFileUrl(fileUrl);

  try {
    if (secureUrl !== fileUrl) {
      const response = await api.get(secureUrl, { responseType: "blob" });
      saveBlob(response.data as Blob, name);
      return;
    }
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    saveBlob(await response.blob(), name);
  } catch {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  }
};

/**
 * Click handler shared by every attachment list: preview what the browser can
 * render, download everything else.
 */
export const openFile = (
  fileUrl: string,
  fileName: string,
  onPreview: () => void
): void => {
  if (isPreviewableFile(fileUrl)) {
    onPreview();
    return;
  }
  downloadFile(fileUrl, fileName);
};

/** Formats that are always handed over as a download, even from a plain link. */
const DOWNLOAD_EXTENSIONS = [
  "doc",
  "docx",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
  "csv",
  "tsv",
  "zip",
  "rar",
  "7z",
  "mp3",
  "wav",
  "m4a",
  "aac",
  "ogg",
  "mp4",
  "mov",
];

/** Whether the URL points at a file we host (as opposed to an external web page). */
export const isUploadedFileUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes("amazonaws.com") || url.includes("/media/");
};

/**
 * Click handler for document / action links, which may be an uploaded file or
 * an external web page: preview what the browser can render, download hosted
 * files and known file formats, and open everything else in a new tab.
 */
export const openLink = (
  url: string,
  title: string,
  onPreview: () => void
): void => {
  if (!url) return;
  if (isPreviewableFile(url)) {
    onPreview();
    return;
  }
  if (isUploadedFileUrl(url) || DOWNLOAD_EXTENSIONS.includes(getFileExtension(url))) {
    downloadFile(url, title);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};
